/**
 * The env-name mapping (field name → env var) of ONE cred_to_env call — built by the worker's tool (the names the
 * model is told) and by the browser's pending session row (the mapping the credential card submits) from the same
 * call input, so the two can't disagree (review round 7: the browser used unsanitized names of its own, which the
 * reply route then refused). Browser-safe: `@papan/sandbox/env-names` is dependency-free.
 */
import { credFieldEnvName, credentialEnvNameError } from '@papan/sandbox/env-names'
import { isHiddenProvider, hiddenCredEnvName } from './hidden-cred-env.js'

export interface CredToEnvCallInput {
  file?: string
  label?: string
  provider?: string
  mode?: 'auto' | 'update' | 'new_env'
  context?: { repo?: string }
  fields?: ReadonlyArray<{ name: string; env?: string }>
}

export function credToEnvMapping(input: CredToEnvCallInput, callId: string): Record<string, string> {
  // Hidden (git) providers: every field maps to the one per-binding internal name the credential helper reads
  const hidden = isHiddenProvider(input.provider) ? hiddenCredEnvName(input.provider!, callId) : null
  const envMapping: Record<string, string> = {}
  for (const field of input.fields ?? []) {
    const env = hidden ?? credFieldEnvName(field, {
      filePath: input.file,
      label: input.label,
      provider: input.provider,
      contextRepo: input.context?.repo,
      includeLabel: input.mode === 'new_env',
    })
    // an OWN property — a model-named field `__proto__` assigned with `=` would hit Object's prototype setter and
    // vanish, and the card would then read Object.prototype as its env name (#1324 owner review, Codex)
    Object.defineProperty(envMapping, field.name, { value: env, enumerable: true, writable: true, configurable: true })
  }
  return envMapping
}

/**
 * #1448 — the ONE-TIME fields of a cred_to_env QUESTION, as its own tool put them on the card: each `prompt` field →
 * the env var the tool told the model (the `credMeta` JSON on the question's first option). A one-time value only ever
 * lands under these names — never under a name an ANSWER chose: any question's answer can carry `oneTime` +
 * `envMapping`, and a name like BASH_ENV in someone's turn env is not a credential. Anything that isn't a credential
 * card the cred_to_env tool raised (`raisedBy`: the question's tool linkage) has none. `request` is the Question request
 * (or a suspension row's stored card event).
 */
export function promptEnvMappingOfQuestion(request: unknown, raisedBy: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  const meta = credCardMeta(request, raisedBy)
  for (const f of meta?.fields ?? []) {
    if (f?.type !== 'prompt' || typeof f.name !== 'string') continue
    const env = meta!.envMapping && Object.prototype.hasOwnProperty.call(meta!.envMapping, f.name) ? meta!.envMapping[f.name] : undefined
    // a name the sandbox accepts for a credential, and never a hidden git provider's internal one (every field of such a
    // card shares it — a code there would replace the token)
    if (typeof env !== 'string' || !env || env.startsWith('__PAPAN_') || credentialEnvNameError({ [f.name]: env })) continue
    Object.defineProperty(out, f.name, { value: env, enumerable: true, writable: true, configurable: true })
  }
  return out
}

/**
 * #1448 D4 — EVERY one-time (`prompt`) field NAME of a cred_to_env question card (the same card linkage as above), with
 * no env-name filter: these fields never take a key's value, whatever their names — `resolveByEntryId` leaves them out.
 */
export function promptFieldNamesOfQuestion(request: unknown, raisedBy: string | null | undefined): string[] {
  return promptFieldNamesOf(credCardMeta(request, raisedBy)?.fields)
}

/** #1448 D4 — the one-time (`prompt`) field names of a cred_to_env CALL's own input (its tool part, server-written). */
export function promptFieldNamesOfCall(input: unknown): string[] {
  return promptFieldNamesOf((input as { fields?: unknown } | null | undefined)?.fields)
}

function promptFieldNamesOf(fields: unknown): string[] {
  if (!Array.isArray(fields)) return []
  const names = fields.flatMap((f) => (f && typeof f === 'object' && (f as { type?: unknown }).type === 'prompt' && typeof (f as { name?: unknown }).name === 'string') ? [(f as { name: string }).name] : [])
  return [...new Set(names)]
}

/** The card meta a cred_to_env question carries (the `credMeta` JSON on its first option) — ONLY a card the
 *  cred_to_env tool raised: the question's tool linkage, which the model can't choose (`header` and the option text are
 *  model-writable: any `question` call can dress up as a credential card). */
function credCardMeta(request: unknown, raisedBy: string | null | undefined): { fields?: Array<{ name?: unknown; type?: unknown }>; envMapping?: Record<string, unknown> } | null {
  if (raisedBy !== 'cred_to_env') return null
  try {
    const r = request as { questions?: Array<{ header?: unknown; options?: Array<{ description?: unknown }> }>; request?: unknown }
    // a suspension row stores the card EVENT ({ type, sessionId, request }) — the question request is inside
    const q = r?.questions ? r : (r?.request as typeof r | undefined)
    const first = q?.questions?.[0]
    if (!first || first.header !== 'Credentials') return null
    const desc = first.options?.[0]?.description
    if (typeof desc !== 'string' || !desc.startsWith('{')) return null
    return JSON.parse(desc)
  } catch { return null /* not a card: no one-time fields */ }
}

/** #1448 D4 — a binding's mapping minus its one-time fields, by the rule `resolveByEntryId` applies: a one-time FIELD
 *  and any variable a one-time field names are left out (a provider's shared `__PAPAN_*` name stays). The readers of a
 *  row's `env_mapping` (compaction, cred_used) list only what the key fills with this. */
export function keyFilledMapping(envMapping: Record<string, string>, promptFields: readonly string[]): Record<string, string> {
  const oneTime = new Set(promptFields)
  if (oneTime.size === 0) return envMapping
  const oneTimeVars = new Set(Object.entries(envMapping).filter(([f, env]) => oneTime.has(f) && !env.startsWith('__PAPAN_')).map(([, env]) => env))
  return Object.fromEntries(Object.entries(envMapping).filter(([f, env]) => !oneTime.has(f) && !oneTimeVars.has(env)))
}

/** Is `envVar` one of the mapping's one-time variables (a code, not the key's value)? */
export function isOneTimeEnv(envMapping: Record<string, string>, promptFields: readonly string[], envVar: string): boolean {
  return Object.values(envMapping).includes(envVar) && !Object.values(keyFilledMapping(envMapping, promptFields)).includes(envVar)
}

/** #1448 — how the model asks for a fresh one-time code: a call of ONLY that field. It has no session row and answers
 *  no key, so the key's binding stays as it is; repeating the whole card would replace the key's row. Follows "call". */
export function freshCodeAsk(code: { name: string; env: string }): string {
  return `cred_to_env again with only that field — cred_to_env({ fields: [{ name: ${JSON.stringify(code.name)}, type: "prompt", env: ${JSON.stringify(code.env)} }] }) — for a fresh code, so the key stays bound; never ask for it in chat.`
}
